import { Fragment, FunctionComponent, h } from 'preact';
import { useCallback, useState } from 'preact/hooks';
import classNames from 'classnames';
import * as dayjs from 'dayjs';
import { isNil } from 'lodash';
import { DeliveryWindows } from '@bringg/react-components';
import { useTranslation } from '../../../hooks/use-translation';
import { DeliveryWindowWithId } from '../../../stores/schedule/schedule-store';
import FooterButton from '../../../components/footer-button/footer-button';
import { ScreenType } from '@bringg/react-components/dist/types/common.consts';

interface Props {
	customerName: string;
	deliveryWindows: DeliveryWindowWithId[];
	onDeliveryWindowSelect: (deliveryWindow: DeliveryWindowWithId) => void;
	onSchedule: () => void;
	isScheduleButtonDisabled: boolean;
	locale?: string;
	timezone?: string;
	showCalendar?: boolean;
	isMobile?: boolean;
}

const CustomerMessage = 'SCHEDULE.CUSTOMER_#_MESSAGE';

const ScheduleDeliveryWindows: FunctionComponent<Props> = ({
	customerName,
	deliveryWindows,
	onDeliveryWindowSelect,
	onSchedule,
	isScheduleButtonDisabled,
	locale,
	timezone,
	showCalendar,
	isMobile
}) => {
	const [loading, setLoading] = useState<boolean>(false);
	const { t } = useTranslation();
	const customerTranslatedMessage = t(CustomerMessage).replace('#', customerName);

	const handleSchedule = useCallback(async () => {
		try {
			setLoading(true);
			await onSchedule();
		} finally {
			setLoading(false);
		}
	}, []);

	return (
		<div
			className={classNames('schedule-delivery-windows', {
				'calendar-view': showCalendar,
				'mobile-view': isMobile
			})}
			data-testid="schedule-delivery-windows"
		>
			{!showCalendar && (
				<Fragment>
					<div className={'customer-message'}>{customerTranslatedMessage}</div>
					<div className={'time-zone-text'}>
						<span>{t('SCHEDULE.TIME_ZONE_TEXT')}: </span>
						{!isNil(timezone) ? <span>{timezone}</span> : <span> {dayjs.tz.guess()}</span>}
					</div>
				</Fragment>
			)}

			<DeliveryWindows
				className={'schedule-delivery-windows-list'}
				deliveryWindowClassName={'schedule-delivery-window'}
				deliveryWindows={deliveryWindows}
				onDeliveryWindowSelect={onDeliveryWindowSelect}
				locale={locale}
				timezone={timezone || undefined}
				showCalendar={showCalendar}
				screenType={isMobile ? ScreenType.MOBILE : ScreenType.DESKTOP}
				initialDate={deliveryWindows[0]?.start_time}
			/>
			<FooterButton
				disabled={isScheduleButtonDisabled}
				onClick={handleSchedule}
				loading={loading}
				buttonTranslateText="SCHEDULE.SCHEDULE_NOW"
			/>
		</div>
	);
};

export default ScheduleDeliveryWindows;
